import { useState, useEffect, useCallback } from 'react'
import { db } from '../lib/supabase'

export function useBusinesses() {
  const [businesses, setBusinesses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBusinesses = useCallback(async () => {
    setLoading(true)
    const { data, error } = await db.getBusinesses()
    if (error) {
      setError(error)
    } else {
      setBusinesses(data)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchBusinesses()
  }, [fetchBusinesses])

  const createBusiness = async (business) => {
    const { data, error } = await db.createBusiness(business)
    if (!error && data) {
      setBusinesses(prev => [...prev, data])
    }
    return { data, error }
  }

  const updateBusiness = async (id, updates) => {
    const { data, error } = await db.updateBusiness(id, updates)
    if (!error && data) {
      setBusinesses(prev => prev.map(b => b.id === id ? data : b))
    }
    return { data, error }
  }

  const deleteBusiness = async (id) => {
    const { error } = await db.deleteBusiness(id)
    if (!error) {
      setBusinesses(prev => prev.filter(b => b.id !== id))
    }
    return { error }
  }

  return {
    businesses,
    loading,
    error,
    refetch: fetchBusinesses,
    createBusiness,
    updateBusiness,
    deleteBusiness,
  }
}

export function useBusiness(id) {
  const [business, setBusiness] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchBusiness = useCallback(async () => {
    if (!id) return
    setLoading(true)
    const { data, error } = await db.getBusiness(id)
    if (error) {
      setError(error)
    } else {
      setBusiness(data)
    }
    setLoading(false)
  }, [id])

  useEffect(() => {
    fetchBusiness()
  }, [fetchBusiness])

  const updateBusiness = async (updates) => {
    const { data, error } = await db.updateBusiness(id, updates)
    if (!error && data) {
      setBusiness(data)
    }
    return { data, error }
  }

  return {
    business,
    loading,
    error,
    refetch: fetchBusiness,
    updateBusiness,
  }
}

export function useBusinessFinancials() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchFinancials = useCallback(async () => {
    setLoading(true)
    const { data, error } = await db.getAllBusinessFinancials()
    if (error) {
      setError(error)
    } else {
      setData(data)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchFinancials()
  }, [fetchFinancials])

  return {
    data,
    businesses: data?.businesses || [],
    totals: data?.totals || { revenue: 0, expenses: 0, profit: 0 },
    loading,
    error,
    refetch: fetchFinancials,
  }
}

export default useBusinesses
